import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import type {ListDataItem} from '@jetbrains/ring-ui/components/list/list'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import PopupMenu from '@jetbrains/ring-ui/components/popup-menu/popup-menu'
import memoizeOne from 'memoize-one'
import {useContext, useId} from 'react'
import * as React from 'react'
import {graphql, useFragment} from 'react-relay'

import {CurrentUserKeyContext} from '../../../../contexts/currentUserKey'
import {useAppSelector} from '../../../../hooks/react-redux'
import {useTheme} from '../../../../hooks/theme/useTheme'
import SvgIcon from '../../../../library/components/SvgIcon/SvgIcon'
import {getIsSakuraUI} from '../../../../selectors'
import {GUEST_ID, SUPER_USER_ID} from '../../../../types'
import {BS} from '../../../../types/BS_types'
import {Size} from '../../../common/Avatar/Avatar.container'
import AvatarRelay from '../../../common/Avatar/Avatar.relay'
import DarkTooltip from '../../../common/DarkTooltip/DarkTooltip'
import Popup from '../../../common/Popup/Popup.lazy'
import {headerUserProfileLinkHint} from '../../../packages/Hints/HintItems/Hints.navigation'
import {useHint} from '../../../packages/Hints/Hints.hooks'
import {isPipelinesEnabledInExclusiveMode} from '../../../pages/PipelinesPages/utils/featureToggles'
import {LINKS} from '../Header.consts'
import headerStyles from '../Header.css'
import ThemeSwitcher from '../ThemeSwitcher/ThemeSwitcher'

import type {HeaderUserFragment$key} from './__generated__/HeaderUserFragment.graphql'
import NodeSwitcher from './NodeSwitcher/NodeSwitcher'

import styles from './HeaderUser.css'

const PopupDirections = [Directions.RIGHT_TOP]
//eslint-disable-next-line new-cap
const logout = () => BS?.Logout?.(LINKS.logout.classic)
const getPopupMenuItems = memoizeOne(
  (isSuperUser: boolean, isSakuraUI: boolean, canShowThemeSwitcher: boolean): ListDataItem[] => {
    const field = isSakuraUI ? 'sakura' : 'classic'
    const favoriteBuilds = LINKS.favoriteBuilds[field]
    const popupMenu: ListDataItem[] = []

    if (!isSuperUser) {
      popupMenu.push({
        key: LINKS.profile.classic,
        label: 'Profile',
        href: LINKS.profile.classic,
      })
    }
    if (canShowThemeSwitcher) {
      popupMenu.push({
        key: 'Appearance',
        rgItemType: PopupMenu.ListProps.Type.CUSTOM,
        className: styles.appearanceContainer,
        template: (
          <Dropdown
            hoverMode
            className={styles.appearance}
            anchor={
              <>
                {'Appearance'}
                <SvgIcon icon="chevron-right" className={styles.appearanceChevron} />
              </>
            }
          >
            <Popup directions={[Directions.RIGHT_TOP]} offset={4} top={16}>
              <ThemeSwitcher />
            </Popup>
          </Dropdown>
        ),
      })
    }

    if (!isSuperUser && !isPipelinesEnabledInExclusiveMode()) {
      popupMenu.push({
        key: favoriteBuilds,
        label: 'Favorite builds',
        href: favoriteBuilds,
      })
    }

    popupMenu.push({
      key: LINKS.logout.classic,
      label: 'Logout',
      onClick: logout,
    })

    return popupMenu
  },
)
const headerUserFragment = graphql`
  fragment HeaderUserFragment on User {
    id
    name
    username
    ...AvatarFragment
  }
`
const HeaderUser = React.memo(() => {
  useHint(headerUserProfileLinkHint)
  const isSakuraUI = useAppSelector(getIsSakuraUI)
  const userKey: HeaderUserFragment$key | null = useContext(CurrentUserKeyContext)
  const user = useFragment(headerUserFragment, userKey)
  const userId = user?.id
  const userName = user?.name ?? user?.username
  const {canShowThemeSwitcher} = useTheme()
  const menuItems =
    userId === GUEST_ID
      ? [
          {
            label: 'Log in',
            href: LINKS.login.classic,
            onClick: logout,
          },
        ]
      : getPopupMenuItems(SUPER_USER_ID === userId, isSakuraUI, canShowThemeSwitcher)
  const id = useId()
  const hasNodeInfo = useAppSelector(state => state.nodeInfo.serverNodes != null)

  return user != null ? (
    <Dropdown
      data-hint-container-id={headerUserProfileLinkHint.id}
      className={styles.user}
      anchor={() => (
        <DarkTooltip
          id={id}
          title={userName}
          popupProps={{directions: [Directions.RIGHT_BOTTOM], top: 0, offset: 24}}
        >
          <button type="button" className={styles.avatarWrapper} aria-labelledby={id}>
            {userId === GUEST_ID ? (
              <SvgIcon icon="user-20px" className={styles.guest} />
            ) : (
              <AvatarRelay
                round
                userKey={user}
                size={Size.Size32}
                className={styles.avatar}
                withOutline={false}
              />
            )}
          </button>
        </DarkTooltip>
      )}
    >
      <PopupMenu
        className={headerStyles.popup}
        directions={PopupDirections}
        top={-16}
        offset={4}
        data={menuItems}
        hint={hasNodeInfo ? <NodeSwitcher /> : null}
      />
    </Dropdown>
  ) : (
    <div className={styles.userLoading} />
  )
})
HeaderUser.displayName = 'HeaderUser'
export default HeaderUser
